import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, usePropertyField, EditableToken } from '@vibesites/runtime/react';

// ============================================================================
// CORT lead-form modal command
//
// The CORT lead form is loaded by the `cort-lead-form` block (which uses the
// property's `custom_cort_lead_form_id` to load the script and registers the
// global `window.Cort_modal`). This CTA simply opens that modal via the
// documented command `window.Cort_modal.open()`.
// ============================================================================
declare global {
  interface Window {
    Cort_modal?: { open?: () => void };
  }
}

/**
 * A primary CTA button that opens the CORT lead form modal.
 *
 * Visibility: the button only renders when the property has the
 * `custom_cort_lead_form_id` field set. When the field is missing/empty the
 * component renders nothing.
 */
export function CortFurnishButton() {
  const leadFormId = usePropertyField('custom_cort_lead_form_id') as string | null | undefined;
  const label = useContentToken('cortfurnishbutton.label');

  // Hide entirely when no CORT lead form is configured for this property.
  if (!leadFormId) return null;

  const handleOpen = () => {
    if (typeof window !== 'undefined' && typeof window.Cort_modal?.open === 'function') {
      window.Cort_modal.open();
    } else {
      console.warn(
        '[CortFurnishButton] CORT modal command (window.Cort_modal.open) is unavailable; ' +
          'ensure the cort-lead-form block is on this page to load the CORT script.',
      );
    }
  };

  return (
    <section className="relative z-20 w-full bg-[var(--vibe-color-background)] flex justify-center px-4 pt-10 md:pt-14">
      <button
        type="button"
        onClick={handleOpen}
        className="cort-cta group/cta relative inline-flex items-center gap-2.5 px-10 py-4 text-sm font-semibold tracking-[0.2em] uppercase text-white overflow-hidden transition-all duration-300"
      >
        <EditableToken
          path="cortfurnishbutton.label"
          defaultValue="Furnish Your Home with CORT"
          as="span"
          className="relative z-10"
        >
          {label}
        </EditableToken>
        <svg
          className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
        </svg>
      </button>

      <style>{`
        .cort-cta {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border: none;
          border-radius: 2px;
          cursor: pointer;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.2),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .cort-cta::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .cort-cta:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow:
            0 4px 12px rgba(0,0,0,0.2),
            0 8px 24px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent);
          transform: translateY(-2px);
        }
        .cort-cta:hover::before { left: 100%; }
        .cort-cta:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CortFurnishButton',
  displayName: 'CORT Furnish Button',
  tokens: {
    'cortfurnishbutton.label': {
      label: 'Button Label',
      type: 'text',
      defaultValue: 'Furnish Your Home with CORT',
    },
  },
};

(CortFurnishButton as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CortFurnishButton;
